import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Undo2, ChevronDown } from 'lucide-react';
import { fetchFFData } from '../../utils/ffBlob';
import { evaluateFilterExpression, type FilterCell } from '../../utils/columnFilter';
import { cleanFilters, type FilterSettings } from '../../utils/filterPresets';
import { FilterPresets } from '../ui/filter-presets';

interface FFData {
  No?: string | number;
  Kode?: string;
  "Nama Perusahaan"?: string;
  "Papan Pencatatan"?: string;
  [key: string]: any;
}

interface DebouncedInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  debounce?: number;
}

function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 300,
  ...props
}: DebouncedInputProps) {
  const [value, setValue] = useState(initialValue);
  const onChangeRef = useRef(onChange);

  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { setValue(initialValue); }, [initialValue]);
  useEffect(() => {
    const timeout = setTimeout(() => { onChangeRef.current(value); }, debounce);
    return () => clearTimeout(timeout);
  }, [value, debounce]);

  return <input {...props} value={value} onChange={e => setValue(e.target.value)} />;
}

type SortKey = string;

const getDisplayName = (raw: string) => {
  let name = raw;
  if (name === 'Kode') return 'Ticker';
  if (name === 'Nama Perusahaan Tercatat') return 'Nama Perusahaan';
  if (name === 'Papan Pencatatan (Tanpa Pemantauan Khusus)') return 'Papan Pencatatan';

  const datePrefixMatch = name.match(/^(.*?)\s*\((periode acuan|terkini)\)\s*-\s*(.*)$/i);
  if (datePrefixMatch) {
    const dateStr = datePrefixMatch[1]!.trim();
    let suffix = datePrefixMatch[3]!.trim();

    // Remove trailing date like "(30 Jun 2026)"
    suffix = suffix.replace(/\s*\(\d{1,2}\s+[a-zA-Z]+\s+\d{4}\)$/i, '');
    
    if (suffix === '% Saham Free Float (FF)') suffix = 'FF';
    else if (suffix === 'Jumlah Pemegang Saham (JPS)') suffix = 'JPS';
    else if (suffix === 'Kewajiban Pemenuhan % Saham Free Float (FF)') return 'Kewajiban FF';
    else if (suffix.includes('Batas Waktu Pemenuhan')) return 'Tenggat';
    else if (suffix.includes('Status Pemenuhan Terkini')) return 'Status Pemenuhan';
    else if (suffix.includes('Kapitalisasi Pasar')) suffix = 'Kapitalisasi';

    return `${dateStr} - ${suffix}`;
  }
  return name;
};

export const parseDate = (dateStr: string) => {
  const months: Record<string, number> = {
    'Januari': 0, 'Februari': 1, 'Maret': 2, 'April': 3, 'Mei': 4, 'Juni': 5,
    'Juli': 6, 'Agustus': 7, 'September': 8, 'Oktober': 9, 'November': 10, 'Desember': 11,
    'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'Jun': 5, 'Jul': 6, 'Ags': 7, 'Sep': 8, 'Okt': 9, 'Nov': 10, 'Des': 11
  };
  const parts = dateStr.trim().split(' ');
  if (parts.length >= 3) {
    const day = parseInt(parts[0]!, 10);
    const month = months[parts[1]!] ?? 0;
    const year = parseInt(parts[2]!, 10);
    return new Date(year, month, day).getTime();
  }
  return 0;
};


export const FreeFloat: React.FC = () => {
  const [data, setData] = useState<FFData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Filters
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  
  // Quarter filters
  const [q1, setQ1] = useState(true);
  const [q2, setQ2] = useState(true);
  const [q3, setQ3] = useState(false);
  const [q4, setQ4] = useState(false);

  // Sorting
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: 'asc' | 'desc' } | null>({
    key: 'Tanggal',
    direction: 'desc'
  });

  const [showQuarterMenu, setShowQuarterMenu] = useState(false);
  const quarterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (quarterRef.current && !quarterRef.current.contains(event.target as Node)) {
        setShowQuarterMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchFFData();
      setData(result.data);
    } catch (err: any) {
      setError(err.message || 'Gagal memuat data Free Float');
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchData();
  }, []);

  const handleSort = (key: SortKey) => {
    setSortConfig(prev => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const allAvailableDates = useMemo(() => {
    if (data.length === 0) return [];
    const dates = new Set<string>();
    Object.keys(data[0]!).forEach(c => {
      const displayName = getDisplayName(c);
      if (displayName.includes(' - ')) {
         dates.add(displayName.split(' - ')[0]!);
      }
    });
    
    return Array.from(dates).sort((a, b) => parseDate(a) - parseDate(b));
  }, [data]);

  // Warna sel persis seperti yang dirender (satu sumber kebenanan utk render & filter)
  const getCellColorClass = (row: any, col: string): string => {
    const displayName = getDisplayName(col);
    const isPercent = displayName.endsWith(' - FF') || displayName.includes('Kewajiban FF');
    const isKapitalisasi = displayName.includes('Kapitalisasi');
    const rawVal = String(row[col] || '');
    const isRed = rawVal.includes('Voluntary Delisting') || rawVal.includes('Forced Delisting') || rawVal.includes('Tidak Memenuhi');
    const isGreen = rawVal.includes('Telah Memenuhi');
    const isYellow = rawVal.includes('Pengecualian');

    let isFFGreen = false;
    let isFFRed = false;
    if (displayName.endsWith(' - FF')) {
      const statusCol = Object.keys(row).find(c => getDisplayName(c) === 'Status Pemenuhan');
      const tenggatCol = Object.keys(row).find(c => getDisplayName(c) === 'Tenggat');

      let combinedStatus = '';
      if (statusCol) combinedStatus += String(row[statusCol] || '').toLowerCase() + ' ';
      if (tenggatCol) combinedStatus += String(row[tenggatCol] || '').toLowerCase();

      if (combinedStatus.includes('telah memenuhi')) {
        isFFGreen = true;
      } else if (combinedStatus.includes('tidak memenuhi') || combinedStatus.includes('delisting')) {
        isFFRed = true;
      }
    }

    let isJPSGreen = false;
    let isJPSRed = false;
    let isJPSYellow = false;
    if (displayName.endsWith('JPS') || displayName.includes('Kapitalisasi')) {
      const datePrefix = displayName.split(' - ')[0]!;
      const dateIdx = allAvailableDates.indexOf(datePrefix);
      if (dateIdx > 0) {
        const prevDatePrefix = allAvailableDates[dateIdx - 1];
        const suffix = displayName.split(' - ')[1];
        const prevCol = Object.keys(row).find(c => {
          const cName = getDisplayName(c);
          return cName === `${prevDatePrefix} - ${suffix}`;
        });

        if (prevCol && row[prevCol] !== undefined && row[prevCol] !== null && row[prevCol] !== '') {
          const currentVal = parseFloat(String(row[col]).replace(/,/g, ''));
          const prevVal = parseFloat(String(row[prevCol]).replace(/,/g, ''));

          if (!isNaN(currentVal) && !isNaN(prevVal)) {
            if (currentVal > prevVal) isJPSGreen = true;
            else if (currentVal < prevVal) isJPSRed = true;
            else isJPSYellow = true;
          }
        }
      }
    }

    const isRightAligned = isKapitalisasi || displayName.includes('JPS') || isPercent;

    let colorClass = 'text-center text-gray-300';

    if (col === 'Kode') {
      colorClass = 'text-center font-medium text-gray-300';
    } else if (isRed) {
      colorClass = 'text-center text-[#ff0000] font-medium';
    } else if (isGreen) {
      colorClass = 'text-center text-[#00ff00] font-medium';
    } else if (isYellow) {
      colorClass = 'text-center text-yellow-400 font-medium';
    } else if (isFFGreen) {
      colorClass = `font-medium text-[#00ff00] ${isRightAligned ? 'text-right pr-2' : 'text-center'}`;
    } else if (isFFRed) {
      colorClass = `font-medium text-[#ff0000] ${isRightAligned ? 'text-right pr-2' : 'text-center'}`;
    } else if (isJPSGreen) {
      colorClass = `font-medium text-[#00ff00] ${isRightAligned ? 'text-right pr-2' : 'text-center'}`;
    } else if (isJPSRed) {
      colorClass = `font-medium text-[#ff0000] ${isRightAligned ? 'text-right pr-2' : 'text-center'}`;
    } else if (isJPSYellow) {
      colorClass = `font-medium text-yellow-400 ${isRightAligned ? 'text-right pr-2' : 'text-center'}`;
    } else if (isRightAligned) {
      colorClass = 'text-right font-medium text-gray-300 pr-2';
    } else if (displayName === 'Status Pemenuhan' || displayName === 'Tenggat') {
      colorClass = 'text-center text-gray-300';
    }

    return colorClass;
  };

  const getNumericFFValue = (val: any, displayName: string): number => {
    if (displayName.endsWith(' - FF') || displayName.includes('Kewajiban FF')) {
      const dec = parseFloat(String(val).replace(',', '.'));
      return isNaN(dec) ? NaN : dec * 100;
    }
    return parseFloat(String(val).replace(/,/g, ''));
  };

  const filteredData = useMemo(() => {
    return data.filter(row => {
      // Filter ekspresi: token warna + kondisi numerik + teks, digabung and/or
      for (const col of Object.keys(columnFilters)) {
        const expr = columnFilters[col];
        if (!expr || !expr.trim()) continue;

        const val = (row as any)[col];
        if (val === undefined || val === null) return false;

        const displayName = getDisplayName(col);
        const isNumericCol = displayName.endsWith(' - FF') || displayName.includes('Kewajiban FF') || displayName.includes('Kapitalisasi') || displayName.includes('JPS');

        const cell: FilterCell = {
          value: isNumericCol ? getNumericFFValue(val, displayName) : NaN,
          text: String(val).toLowerCase(),
          colorClass: getCellColorClass(row, col),
          isNumeric: isNumericCol,
        };
        if (!evaluateFilterExpression(expr, cell)) return false;
      }
      return true;
    });
  }, [data, columnFilters]);

  const columns = useMemo(() => {
    if (data.length === 0) return [];
    let cols = Object.keys(data[0]!).filter(c => c !== 'No' && c !== '' && c !== 'Nama Perusahaan Tercatat' && c !== 'Nama Perusahaan');

    const getScore = (col: string) => {
      const name = getDisplayName(col);
      if (name === 'Ticker') return 1;
      if (name === 'Papan Pencatatan') return 2;
      if (name === 'Kewajiban FF') return 3;
      if (name === 'Tenggat') return 4;
      if (name === 'Status Pemenuhan') return 100;
      
      const parts = name.split(' - ');
      if (parts.length > 1) {
        return 50; 
      }
      return 99;
    };

    cols.sort((a, b) => {
      const nameA = getDisplayName(a);
      const nameB = getDisplayName(b);
      const scoreA = getScore(a);
      const scoreB = getScore(b);
      
      if (scoreA !== scoreB) return scoreA - scoreB;
      
      if (scoreA === 50) {
        const prefixA = nameA.split(' - ')[0]!;
        const prefixB = nameB.split(' - ')[0]!;
        if (prefixA !== prefixB) {
           return parseDate(prefixA) - parseDate(prefixB); 
        } else {
           const getSubScore = (n: string) => {
             if (n.includes('Kapitalisasi')) return 1;
             if (n.includes('JPS')) return 2;
             if (n.endsWith(' - FF')) return 3;
             return 99;
           };
           return getSubScore(nameA) - getSubScore(nameB);
        }
      }
      return 0;
    });

    return cols;
  }, [data]);

  const groupedColumns = useMemo(() => {
    const result: { title: string; colSpan: number; cols: string[]; isGroup: boolean }[] = [];
    
    let currentGroup = '';
    let currentCols: string[] = [];
    
    columns.forEach(col => {
      const displayName = getDisplayName(col);
      const parts = displayName.split(' - ');
      
      if (parts.length > 1) {
        const groupName = parts[0]!;
        if (groupName !== currentGroup) {
          if (currentCols.length > 0) {
            result.push({ title: currentGroup, colSpan: currentCols.length, cols: currentCols, isGroup: true });
          }
          currentGroup = groupName;
          currentCols = [col];
        } else {
          currentCols.push(col);
        }
      } else {
        if (currentCols.length > 0) {
          result.push({ title: currentGroup, colSpan: currentCols.length, cols: currentCols, isGroup: true });
          currentCols = [];
          currentGroup = '';
        }
        result.push({ title: displayName, colSpan: 1, cols: [col], isGroup: false });
      }
    });
    
    if (currentCols.length > 0) {
      result.push({ title: currentGroup, colSpan: currentCols.length, cols: currentCols, isGroup: true });
    }
    
    return result;
  }, [columns]);

  // Presets: the CSV's column names carry the dates of its periods and change with each new publication, so a preset
  // keeps a dated column by its period counted from the newest ("terbaru - FF", "terbaru-1 - JPS") and the others by
  // their header ("Ticker", "Kewajiban FF"); it then still applies after the data moves on
  const presetKeys = useMemo(() => {
    const toKey = new Map<string, string>(), toCol = new Map<string, string>();
    for (const col of columns) {
      const name = getDisplayName(col);
      const cut = name.indexOf(' - ');
      const i = cut < 0 ? -1 : allAvailableDates.indexOf(name.slice(0, cut));
      const age = allAvailableDates.length - 1 - i;
      let key = i < 0 ? name : `${age ? `terbaru-${age}` : 'terbaru'} - ${name.slice(cut + 3)}`;
      if (toCol.has(key)) key = col;  // two columns under one header: the second keeps its own name
      toKey.set(col, key);
      toCol.set(key, col);
    }
    return { toKey, toCol };
  }, [columns, allAvailableDates]);

  const presetCurrent = useMemo<FilterSettings>(() => {
    const key = (col: string) => presetKeys.toKey.get(col) ?? col;
    return {
      filters: Object.fromEntries(Object.entries(cleanFilters(columnFilters)).map(([col, expr]) => [key(col), expr])),
      // the menu's default sort ('Tanggal') is no column of the CSV: it counts as no sort
      sort: sortConfig && presetKeys.toKey.has(sortConfig.key) ? { key: key(sortConfig.key), direction: sortConfig.direction } : null,
      view: { q1, q2 },  // Q3 / Q4 cannot be picked yet
    };
  }, [presetKeys, columnFilters, sortConfig, q1, q2]);

  // The quarters are not part of the fetch, so a preset never needs a refetch. One picked before the table has loaded
  // is put back again once its columns are there.
  const pendingPreset = useRef<FilterSettings | null>(null);
  const applyPreset = (s: FilterSettings) => {
    if (!columns.length) {
      pendingPreset.current = s;
      return;
    }
    const filters: Record<string, string> = {};
    for (const [key, expr] of Object.entries(cleanFilters(s?.filters))) {
      const col = presetKeys.toCol.get(key);
      if (col) filters[col] = expr;
    }
    setColumnFilters(filters);
    const so = s?.sort;
    const sortCol = so && typeof so.key === 'string' && (so.direction === 'asc' || so.direction === 'desc') ? presetKeys.toCol.get(so.key) : undefined;
    setSortConfig(sortCol && so ? { key: sortCol, direction: so.direction } : { key: 'Tanggal', direction: 'desc' });
    const v: Record<string, unknown> = s?.view ?? {};
    const vq1 = v['q1'], vq2 = v['q2'];
    if (typeof vq1 === 'boolean') setQ1(vq1);
    if (typeof vq2 === 'boolean') setQ2(vq2);
  };
  useEffect(() => {
    const s = pendingPreset.current;
    if (s && columns.length) {
      pendingPreset.current = null;
      applyPreset(s);
    }
  }, [columns]);  // eslint-disable-line react-hooks/exhaustive-deps
  const presetLabel = (key: string) => {
    const col = presetKeys.toCol.get(key);
    return col ? getDisplayName(col) : key;
  };

  const sortedData = useMemo(() => {
    if (!sortConfig) return filteredData;
    return [...filteredData].sort((a, b) => {
      let aVal = (a as any)[sortConfig.key] ?? '';
      let bVal = (b as any)[sortConfig.key] ?? '';

      // Try numeric sort first if applicable
      const aNum = parseFloat(String(aVal).replace(',', '.'));
      const bNum = parseFloat(String(bVal).replace(',', '.'));
      if (!isNaN(aNum) && !isNaN(bNum)) {
        if (aNum < bNum) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aNum > bNum) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      }

      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);

  const SortIndicator = ({ col }: { col: SortKey }) => {
    if (sortConfig?.key !== col) return null;
    return <span className="text-[10px]">{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>;
  };

  const thClass = (_col: SortKey) =>
    `px-2 py-0 text-center whitespace-nowrap cursor-pointer select-none hover:bg-white/10 transition-colors`;

  const formatCellValue = (col: string, val: any) => {
    if (val === undefined || val === null || val === '') return '-';
    
    let strVal = String(val);
    const displayName = getDisplayName(col);

    if (displayName.endsWith(' - FF') || displayName.includes('%') || displayName.includes('Kewajiban FF')) {
      const num = parseFloat(strVal.replace(',', '.'));
      if (!isNaN(num)) {
        const percent = num * 100;
        return `${percent.toFixed(2)}%`;
      }
    }
    
    if (strVal === 'Telah Memenuhi Peraturan I-A') return 'Memenuhi';
    if (strVal === 'Telah Memenuhi Peraturan I-V') return 'Memenuhi';
    if (strVal === 'Tidak Memenuhi Peraturan I-V') return 'Tidak Memenuhi';
    if (strVal === 'Pengecualian sesuai ketentuan V.1.4. Peraturan I-A') return 'Pengecualian';
    if (strVal === 'Pengecualian sesuai ketentuan V.1.4. Peraturan I-V') return 'Pengecualian';
    if (strVal.includes(' 00:00:00')) strVal = strVal.replace(' 00:00:00', '');

    if (displayName.includes('Kapitalisasi')) {
      const num = parseFloat(strVal.replace(/,/g, ''));
      if (!isNaN(num)) {
        if (num >= 1e12) return (num / 1e12).toFixed(2) + ' T';
        if (num >= 1e9) return (num / 1e9).toFixed(2) + ' B';
        if (num >= 1e6) return (num / 1e6).toFixed(2) + ' M';
        if (num >= 1e3) return (num / 1e3).toFixed(2) + ' K';
        return num.toString();
      }
    }

    if (displayName.includes('JPS')) {
      const num = parseInt(strVal.replace(/[,.]/g, ''), 10);
      if (!isNaN(num)) {
        return num.toLocaleString('en-US');
      }
    }
    
    return strVal;
  };

  return (
    <div className="flex flex-col h-full bg-[#0f172a] text-white overflow-hidden relative">
      {loading && (
        <div className="absolute top-0 left-0 w-full h-1 z-50 overflow-hidden bg-[#3a4252]">
          <div className="w-full h-full bg-blue-500 animate-[loading_1.5s_ease-in-out_infinite]" style={{ transformOrigin: 'left' }} />
        </div>
      )}
      <style>{`
        @keyframes loading {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>

      {/* ── Top Control Bar ── */}
      <div className="bg-card border-b border-[#3a4252] px-4 py-3 shrink-0 lg:sticky lg:top-0 lg:z-40">
        <div className="flex flex-col md:flex-row md:flex-wrap items-center gap-4 md:gap-x-8 md:gap-y-4">
          
          {/* Quarter Dropdown */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium whitespace-nowrap text-white">Quarter:</label>
            <div className="relative" ref={quarterRef}>
              <button
                onClick={() => setShowQuarterMenu(!showQuarterMenu)}
                className="h-8 px-3 min-w-[130px] bg-background border border-input rounded text-sm text-white flex items-center justify-between gap-2 hover:border-primary/50 transition-colors"
              >
                <span className="truncate text-left flex-1">
                  {[q1 && 'Q1', q2 && 'Q2', q3 && 'Q3', q4 && 'Q4'].filter(Boolean).join(', ') || 'None'}
                </span>
                <ChevronDown className="w-3.5 h-3.5 opacity-70 flex-shrink-0" />
              </button>
              
              {showQuarterMenu && (
              <div className="absolute top-full left-0 mt-1 w-32 bg-[#1e293b] border border-[#3a4252] rounded-md shadow-lg z-50 p-3 flex flex-col gap-3">
                <label className="flex items-center gap-2 text-sm text-white cursor-pointer hover:text-blue-400 transition-colors">
                  <input type="checkbox" checked={q1} onChange={(e) => setQ1(e.target.checked)} className="accent-blue-500 w-4 h-4 cursor-pointer" />
                  Q1
                </label>
                <label className="flex items-center gap-2 text-sm text-white cursor-pointer hover:text-blue-400 transition-colors">
                  <input type="checkbox" checked={q2} onChange={(e) => setQ2(e.target.checked)} className="accent-blue-500 w-4 h-4 cursor-pointer" />
                  Q2
                </label>
                <label className="flex items-center gap-2 text-sm cursor-not-allowed opacity-50 text-gray-500">
                  <input type="checkbox" checked={q3} disabled className="accent-blue-500 w-4 h-4 cursor-not-allowed" />
                  Q3
                </label>
                <label className="flex items-center gap-2 text-sm cursor-not-allowed opacity-50 text-gray-500">
                  <input type="checkbox" checked={q4} disabled className="accent-blue-500 w-4 h-4 cursor-not-allowed" />
                  Q4
                </label>
              </div>
            )}
          </div>
        </div>

          {/* Controls */}
          <div className="flex items-center gap-2 w-full md:w-auto md:ml-auto">
            <button
              onClick={fetchData}
              disabled={loading}
              className="h-8 px-4 bg-white text-black hover:bg-gray-200 rounded text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50"
            >
              Show
            </button>
            <button
              onClick={() => {
                setColumnFilters({});
                setSortConfig({ key: 'Tanggal', direction: 'desc' });
                setQ1(true); setQ2(true); setQ3(false); setQ4(false);
              }}
              className="h-8 px-3 bg-transparent border border-white/20 text-white hover:bg-white/10 rounded text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
            >
              <Undo2 className="w-3.5 h-3.5 rotate-180" />
              Reset
            </button>
            {/* at the right edge on a phone too: the Presets list opens leftwards from the button */}
            <div className="ml-auto md:ml-0">
              <FilterPresets page="free-float" current={presetCurrent} onApply={applyPreset} label={presetLabel} />
            </div>
          </div>
        </div>
      </div>

      {/* ── Error / Loading ── */}
      {error && (
        <div className="p-4 m-4 bg-red-900/50 border border-red-500 text-red-200 rounded">
          {error}
        </div>
      )}

      {/* ── Table ── */}
      <div className="flex flex-col bg-black flex-auto min-h-0 w-full">
        <div className="flex-auto overflow-y-auto overflow-x-auto" style={{ willChange: 'transform' }}>
          <table className="w-full text-[12px] tabular-nums min-w-[700px] leading-tight border-separate border-spacing-0">
            <thead className="sticky top-0 bg-gray-900/80 backdrop-blur-md text-white font-bold z-10" style={{ transform: 'translateZ(0)' }}>
              {/* Top Header Row (Groups & Ungrouped) */}
              <tr className="h-[22px] divide-x divide-white/30 border-b border-white/30">
                {groupedColumns.map((group, i) => (
                  group.isGroup ? (
                    <th key={i} colSpan={group.colSpan} className="px-2 py-0 text-center border-b border-white/30 whitespace-nowrap bg-white/5">
                      {group.title}
                    </th>
                  ) : (
                    <th key={i} rowSpan={2} onClick={() => handleSort(group.cols[0]!)} className={`${thClass(group.cols[0]!)} align-middle ${i > 0 ? 'border-l border-white/30' : ''} ${group.cols[0] === 'Kode' ? 'w-[60px]' : ''}`}>
                      {group.title}<SortIndicator col={group.cols[0]!} />
                    </th>
                  )
                ))}
              </tr>
              {/* Bottom Header Row (Sub-columns) */}
              <tr className="h-[22px] divide-x divide-white/30 border-b border-white/30">
                {groupedColumns.map(group => (
                  group.isGroup && group.cols.map(col => {
                    const subName = getDisplayName(col).split(' - ')[1];
                    return (
                      <th key={col} onClick={() => handleSort(col)} className={thClass(col)}>
                        {subName}<SortIndicator col={col} />
                      </th>
                    );
                  })
                ))}
              </tr>
              {/* Filter Row */}
              <tr className="bg-gray-800/80 backdrop-blur-md h-[22px] divide-x divide-white/30">
                {columns.map(col => (
                  <td key={col} className="px-1 py-0 min-w-[120px]">
                    <DebouncedInput
                      type="text"
                      placeholder="Filter"
                      className="w-full bg-transparent text-[12px] text-center text-white placeholder:text-gray-500 focus:outline-none"
                      value={columnFilters[col] || ''}
                      onChange={val => setColumnFilters(prev => ({ ...prev, [col]: val }))}
                    />
                  </td>
                ))}
              </tr>
            </thead>
            
            <tbody>
              {sortedData.length > 0 ? (
                sortedData.map((row, idx) => (
                  <tr key={idx} className="hover:bg-white/[0.25] transition-colors h-[22px] divide-x divide-white/30">
                    {columns.map(col => {
                      const rawVal = String(row[col] || '');
                      const companyName = String(row['Nama Perusahaan Tercatat'] || row['Nama Perusahaan'] || '');
                      const tooltipVal = col === 'Kode' ? companyName : rawVal;
                      const colorClass = getCellColorClass(row, col);

                      return (
                        <td key={col} title={tooltipVal} className={`px-2 py-0 truncate max-w-[240px] ${colorClass}`}>
                          {formatCellValue(col, row[col])}
                        </td>
                      );
                    })}
                  </tr>
                ))
              ) : !loading ? (
                <tr>
                  <td colSpan={Math.max(columns.length, 1)} className="text-center py-8 text-gray-500">
                    Tidak ada data ditemukan.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
